import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, DollarSign, Building2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useBrand } from '@/config/brands';

export interface CodeLookupResult {
  code: string;
  codeType: string;
  description: string;
  consumerName: string;
  medianPrice: number;
  totalHospitals: number;
}

interface ScenarioCodeLookupProps {
  onCodeSelected: (result: CodeLookupResult) => void;
  placeholder?: string;
}

export function ScenarioCodeLookup({
  onCodeSelected,
  placeholder = 'Search CPT, DRG, HCPCS, or REV codes...',
}: ScenarioCodeLookupProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: results = [], isLoading } = useQuery({
    queryKey: ['scenario-code-lookup', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim() || debouncedQuery.length < 2) return [];

      const searchResults: CodeLookupResult[] = [];
      const searchTerm = debouncedQuery.trim();

      // Search CPT codes
      const { data: cptData } = await supabase
        .from('procedures')
        .select('code, description, llm_quick_name')
        .eq('code_type', 'CPT')
        .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(8);

      if (cptData && cptData.length > 0) {
        const uniqueCodes = new Map<string, typeof cptData[0]>();
        cptData.forEach(item => { if (!uniqueCodes.has(item.code)) uniqueCodes.set(item.code, item); });

        const codes = Array.from(uniqueCodes.keys());
        const { data: pricing } = await supabase
          .from('cpt_pricing_averages')
          .select('cpt_code, median_gross_charge, total_hospitals')
          .in('cpt_code', codes)
          .eq('setting_type', 'all_settings')
          .order('total_hospitals', { ascending: false });

        const priceMap = new Map<string, { median: number; hospitals: number }>();
        pricing?.forEach(p => {
          if (!priceMap.has(p.cpt_code)) {
            priceMap.set(p.cpt_code, {
              median: p.median_gross_charge || 0,
              hospitals: p.total_hospitals || 0,
            });
          }
        });

        Array.from(uniqueCodes.values()).slice(0, 5).forEach(item => {
          const price = priceMap.get(item.code);
          searchResults.push({
            code: item.code,
            codeType: 'CPT',
            description: item.description || '',
            consumerName: item.llm_quick_name || item.description || '',
            medianPrice: price?.median || 0,
            totalHospitals: price?.hospitals || 0,
          });
        });
      }

      // Search DRG codes
      const { data: drgData } = await supabase
        .from('drg_codes')
        .select('drg_code, description, llm_quick_name')
        .or(`drg_code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(5);

      if (drgData && drgData.length > 0) {
        const drgCodes = drgData.map(d => d.drg_code.startsWith('MS-') ? d.drg_code : `MS-${d.drg_code}`);
        const { data: drgPricing } = await supabase
          .from('drg_pricing_averages')
          .select('drg_code, median_gross_charge, total_hospitals')
          .in('drg_code', drgCodes)
          .order('total_hospitals', { ascending: false });

        const priceMap = new Map(drgPricing?.map(p => [p.drg_code, p]) || []);

        drgData.slice(0, 3).forEach(item => {
          const lookupKey = item.drg_code.startsWith('MS-') ? item.drg_code : `MS-${item.drg_code}`;
          const pricing = priceMap.get(lookupKey);
          searchResults.push({
            code: item.drg_code,
            codeType: 'DRG',
            description: item.description || '',
            consumerName: item.llm_quick_name || item.description || '',
            medianPrice: pricing?.median_gross_charge || 0,
            totalHospitals: pricing?.total_hospitals || 0,
          });
        });
      }

      // Search HCPCS codes
      const { data: hcpcsData } = await supabase
        .from('procedures')
        .select('code, description, llm_quick_name')
        .eq('code_type', 'HCPCS')
        .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(5);

      if (hcpcsData && hcpcsData.length > 0) {
        const uniqueCodes = new Map<string, typeof hcpcsData[0]>();
        hcpcsData.forEach(item => { if (!uniqueCodes.has(item.code)) uniqueCodes.set(item.code, item); });

        const codes = Array.from(uniqueCodes.keys());
        const { data: pricing } = await supabase
          .from('hcpcs_pricing_averages')
          .select('hcpcs_code, median_gross_charge, total_hospitals')
          .in('hcpcs_code', codes)
          .eq('setting_type', 'all_settings')
          .order('total_hospitals', { ascending: false });

        const priceMap = new Map<string, { median: number; hospitals: number }>();
        pricing?.forEach(p => {
          if (!priceMap.has(p.hcpcs_code)) {
            priceMap.set(p.hcpcs_code, {
              median: p.median_gross_charge || 0,
              hospitals: p.total_hospitals || 0,
            });
          }
        });

        Array.from(uniqueCodes.values()).slice(0, 3).forEach(item => {
          const price = priceMap.get(item.code);
          searchResults.push({
            code: item.code,
            codeType: 'HCPCS',
            description: item.description || '',
            consumerName: item.llm_quick_name || item.description || '',
            medianPrice: price?.median || 0,
            totalHospitals: price?.hospitals || 0,
          });
        });
      }

      // Search Revenue codes
      const { data: revData } = await supabase
        .from('procedures')
        .select('code, description, llm_quick_name')
        .eq('code_type', 'REV')
        .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(5);

      if (revData && revData.length > 0) {
        const uniqueCodes = new Map<string, typeof revData[0]>();
        revData.forEach(item => { if (!uniqueCodes.has(item.code)) uniqueCodes.set(item.code, item); });

        const codes = Array.from(uniqueCodes.keys());
        const { data: pricing } = await supabase
          .from('revenue_pricing_averages')
          .select('revenue_code, median_charge, total_hospitals')
          .in('revenue_code', codes)
          .order('total_hospitals', { ascending: false });

        const priceMap = new Map(pricing?.map(p => [p.revenue_code, p]) || []);

        Array.from(uniqueCodes.values()).slice(0, 3).forEach(item => {
          const price = priceMap.get(item.code);
          searchResults.push({
            code: item.code,
            codeType: 'REV',
            description: item.description || '',
            consumerName: item.llm_quick_name || item.description || '',
            medianPrice: price?.median_charge || 0,
            totalHospitals: price?.total_hospitals || 0,
          });
        });
      }

      return searchResults;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  const handleSelect = (result: CodeLookupResult) => {
    onCodeSelected(result);
    setQuery('');
    setShowDropdown(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const codeTypeColors: Record<string, string> = {
    CPT: isAhp ? 'bg-blue-100 text-blue-700' : 'bg-blue-500/20 text-blue-400',
    DRG: isAhp ? 'bg-purple-100 text-purple-700' : 'bg-purple-500/20 text-purple-400',
    HCPCS: isAhp ? 'bg-green-100 text-green-700' : 'bg-green-500/20 text-green-400',
    REV: isAhp ? 'bg-amber-100 text-amber-700' : 'bg-amber-500/20 text-amber-400',
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => { if (query.length >= 2) setShowDropdown(true); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="pl-10 pr-10 text-sm font-mono"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg max-h-[320px] overflow-y-auto thin-scrollbar ${
            isAhp
              ? 'bg-white border border-gray-200'
              : 'bg-black/95 border border-matrix-green/20'
          }`}
        >
          {results.map((result, idx) => (
            <button
              key={`${result.codeType}-${result.code}`}
              onClick={() => handleSelect(result)}
              className={`w-full px-3 py-2.5 text-left transition-colors border-b last:border-b-0 ${
                idx === selectedIndex
                  ? isAhp ? 'bg-blue-50' : 'bg-matrix-green/10'
                  : isAhp ? 'hover:bg-gray-50 border-gray-100' : 'hover:bg-matrix-green/5 border-matrix-green/10'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${codeTypeColors[result.codeType] || ''}`}>
                      {result.codeType}
                    </span>
                    <span className={`font-mono text-sm font-bold ${isAhp ? 'text-blue-600' : 'text-matrix-green'}`}>
                      {result.code}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {result.consumerName || result.description}
                  </p>
                </div>

                <div className="text-right shrink-0">
                  {result.medianPrice > 0 && (
                    <div className="flex items-center gap-1 text-xs">
                      <DollarSign className="w-3 h-3 text-muted-foreground" />
                      <span className={`font-mono font-bold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                        {result.medianPrice >= 1000
                          ? `$${(result.medianPrice / 1000).toFixed(1)}k`
                          : `$${result.medianPrice.toFixed(0)}`
                        }
                      </span>
                    </div>
                  )}
                  {result.totalHospitals > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-0.5">
                      <Building2 className="w-2.5 h-2.5" />
                      <span>{result.totalHospitals.toLocaleString()} hospitals</span>
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showDropdown && query.length >= 2 && !isLoading && results.length === 0 && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg p-4 text-center ${
          isAhp ? 'bg-white border border-gray-200' : 'bg-black/95 border border-matrix-green/20'
        }`}>
          <p className="text-xs text-muted-foreground">No codes found for "{query}"</p>
          <p className="text-[10px] text-muted-foreground mt-1">You can still enter the code manually below</p>
        </div>
      )}
    </div>
  );
}
